import React, { useEffect, useState } from 'react';
import Dashboard from './components/Dashboard/DashBoard';
import OperationLogs from './components/OperationLogList';
import LoginPage from './LoginPage'; // adjust path if different
import { OperationLog } from './types/OperationLog';

// 🔹 Import logos (place files in src/assets/)
import appLogo from './assets/sm.png';

type View = 'login' | 'dashboard' | 'logs';
const LOGS_URL = '/api/operations/recent?limit=10';
const AUTH_URL = '/api/auth/sign-in';

const App: React.FC = () => {
  // keep token in state so UI reacts right away
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
  const [view, setView] = useState<View>(token ? 'dashboard' : 'login');

  const [logs, setLogs] = useState<OperationLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ---- Login handler expected by LoginPage ----
  const handleLogin = async (username: string, password: string) => {
    setError(null);
    try {
      const res = await fetch(AUTH_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      });

      const text = await res.text();
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${text}`);

      const json = text ? JSON.parse(text) : {};
      // support common shapes: {token} or {access_token}
      const newToken: string | undefined = json?.token ?? json?.access_token;
      if (!newToken) throw new Error('Token not found in response');

      localStorage.setItem('token', newToken);
      setToken(newToken);
      setView('dashboard');
    } catch (e: any) {
      setError(e?.message || 'Login failed');
    }
  };

  // ---- Logs loader ----
  async function loadLogs() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(LOGS_URL, {
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
      });

      const text = await res.text();
      console.log('LOGS =>', res.status, LOGS_URL, text.slice(0, 200));

      if (!res.ok) throw new Error(`HTTP ${res.status}: ${text}`);

      const json = text ? JSON.parse(text) : [];
      const arr = Array.isArray(json)
        ? json
        : json?.operations ?? json?.data?.operations ?? [];

      setLogs((arr || []) as OperationLog[]);
    } catch (e: any) {
      setLogs([]);
      setError(e?.message || 'خطا در دریافت لاگ‌ها');
    } finally {
      setLoading(false);
    }
  }

  // prefetch logs after login so dashboard can show counts (optional)
  useEffect(() => {
    if (token) loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setView('login');
    setLogs([]);
  };

  return (
    <>
      {view === 'login' && (
        <>
          {error && (
            <div style={{ maxWidth: 720, margin: '16px auto', color: '#b71c1c', textAlign: 'center' }}>
              {error}
            </div>
          )}
          <LoginPage onLogin={handleLogin} />
        </>
      )}

      {view === 'dashboard' && token && (
        <Dashboard
          onRecentActivitiesClick={async () => {
            await loadLogs();   // always fetch fresh before showing
            setView('logs');
          }}
          // 🔹 pass logos to Dashboard sidebar
          logoSrc={appLogo}
        />
      )}

      {view === 'logs' && token && (
        <div dir="rtl">
          <div style={{ padding: 12, textAlign: 'right', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              onClick={() => setView('dashboard')}
              style={{ border: '1px solid #d0d7de', borderRadius: 10, padding: '6px 10px' }}
            >
              بازگشت
            </button>
            <button
              onClick={handleLogout}
              style={{ border: '1px solid #f1b0b7', borderRadius: 10, padding: '6px 10px' }}
            >
              خروج
            </button>
          </div>

          <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 12px 24px' }}>
            <OperationLogs
              logs={logs}
              loading={loading}
              error={error}
              onLogClick={(log) => console.log('clicked log', log)}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default App;
